import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import type { PhotoReview } from '../components/product/PhotoReviewsPreview';

interface ApiReviewItem {
  id: number;
  rating: number;
  authorName: string;
  text: string;
  imageUrl: string | null;
  verified: boolean;
  createdAt: string;
}

interface ApiReviewListResponse {
  items: ApiReviewItem[];
  averageRating: number | null;
  totalCount: number;
}

interface State {
  reviews: PhotoReview[];
  averageRating: number | null;
  totalCount: number;
  loading: boolean;
  error: string | null;
}

interface UseProductReviews extends State {
  refetch: () => void;
}

/**
 * Pulls reviews for a single product from the storefront API. Empty array
 * is the resting state — `PhotoReviewsPreview` knows to render nothing
 * in that case, so callers don't need to gate on length.
 */
export function useProductReviews(productId?: number | null): UseProductReviews {
  const [state, setState] = useState<State>({
    reviews: [],
    averageRating: null,
    totalCount: 0,
    loading: !!productId,
    error: null,
  });
  const [bump, setBump] = useState(0);

  const refetch = useCallback(() => setBump((n) => n + 1), []);

  useEffect(() => {
    if (!productId) {
      setState({ reviews: [], averageRating: null, totalCount: 0, loading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    api
      .get<ApiReviewListResponse>(`/store/products/${productId}/reviews`)
      .then((res) => {
        if (cancelled) return;
        setState({
          reviews: res.data.items.map((r) => ({
            id: r.id,
            rating: r.rating,
            authorName: r.authorName,
            text: r.text,
            imageUrl: r.imageUrl ?? undefined,
            verified: r.verified,
            createdAt: r.createdAt,
          })),
          averageRating: res.data.averageRating,
          totalCount: res.data.totalCount,
          loading: false,
          error: null,
        });
      })
      .catch((err: any) => {
        if (cancelled) return;
        setState({
          reviews: [],
          averageRating: null,
          totalCount: 0,
          loading: false,
          error: err?.message ?? 'Could not load reviews',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [productId, bump]);

  return { ...state, refetch };
}
